import { RuleAction } from '@wix/ambassador-promote-accessibility-v1-accessibility-report/types';
import { useErrorHandler, useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { useCallback } from 'react';
import { showSaveErrorModal } from '../../../components/modals/show-save-error-modal';
import { useDomOrderOrganizeConfirmationModal } from '../../../components/wizard-footer/dom-order-organize-confirmation-modal';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useAnalyzeFullSiteQuery } from '../../../queries/use-analyze-full-site-query';
import { isEditor3Host, isStudioHost } from '../../../utils/get-editor-host';
import { useUpdateDomOrderRuleData } from './use-update-dom-order-rule-data';
import { waitForAutoDomOrderEnabled } from './wait-for-auto-dom-enabled';
export const useDomOrderFix = () => {
  const biLogger = useBiLogger();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    t
  } = useTranslation();
  const {
    getResolvedError
  } = useErrorHandler();
  const {
    createDomOrderRuleData
  } = useUpdateDomOrderRuleData();
  const {
    experiments
  } = useExperiments();
  const enableRTLDomOrder = experiments.enabled('specs.responsive-editor.EnableRTLDomOrder');
  const {
    refetch: analyzeFullSite
  } = useAnalyzeFullSiteQuery();
  const {
    showOrganizeConfirmationModal
  } = useDomOrderOrganizeConfirmationModal();
  const handleManualFix = useCallback(async _ref => {
    let {
      isManuallyCompleted
    } = _ref;
    const updateRuleData = async () => {
      try {
        await a11yWizardAdapter.disableSiteAutoDOMOrder();
        await createDomOrderRuleData(RuleAction.COMPLETED_MANUALLY);
      } catch (error) {
        await showSaveErrorModal({
          a11yWizardAdapter,
          biLogger,
          editorHost,
          errorMessage: getResolvedError(error).message,
          handleTryAgain: updateRuleData,
          t
        });
      }
    };
    if (!isManuallyCompleted) {
      try {
        await updateRuleData();
        await analyzeFullSite();
      } catch (error) {
        await showSaveErrorModal({
          a11yWizardAdapter,
          biLogger,
          editorHost,
          errorMessage: getResolvedError(error).message,
          handleTryAgain: updateRuleData,
          t
        });
      }
    }
  }, [a11yWizardAdapter, createDomOrderRuleData, biLogger, editorHost, getResolvedError, t, analyzeFullSite]);
  const handleAutoFix = useCallback(async _ref2 => {
    let {
      progressBarHandler: _progressBarHandler,
      screenType: _screenType
    } = _ref2;
    const isLangAndSiteDirectionMismatch = enableRTLDomOrder && !a11yWizardAdapter.getIsSiteDirectionMatchingLanguageDirection();
    const updateRuleData = async () => {
      try {
        await createDomOrderRuleData(RuleAction.COMPLETED_AUTOMATICALLY);
      } catch (error) {
        await showSaveErrorModal({
          a11yWizardAdapter,
          biLogger,
          editorHost,
          errorMessage: getResolvedError(error).message,
          handleTryAgain: updateRuleData,
          t
        });
      }
    };
    if (isStudioHost(editorHost) || isEditor3Host(editorHost)) {
      const isDomOrderInProgress = await a11yWizardAdapter.getIsDomReorderInProgress();
      if (isDomOrderInProgress) {
        await a11yWizardAdapter.continueReorganizeAllPagesDOMOrder();
      } else if (isLangAndSiteDirectionMismatch) {
        const modalResult = await showOrganizeConfirmationModal();
        if (modalResult === 'cancel') {
          return;
        }
      } else {
        await a11yWizardAdapter.reorganizeAllPagesDOMOrder(false, false);
        await a11yWizardAdapter.enableSiteAutoDOMOrder();
        await waitForAutoDomOrderEnabled(a11yWizardAdapter);
      }
    } else {
      await a11yWizardAdapter.enableSiteAutoDOMOrder();
      await a11yWizardAdapter.reorganizeAllPagesDOMOrder();
      await a11yWizardAdapter.enableSiteAutoDOMOrder();
      await waitForAutoDomOrderEnabled(a11yWizardAdapter);
      const isLangDirMatchAfterEnable = a11yWizardAdapter.getIsSiteDirectionMatchingLanguageDirection();
      if (!isLangDirMatchAfterEnable && enableRTLDomOrder) {
        await a11yWizardAdapter.flipSiteDomDirectionFlag();
      }
    }
    await updateRuleData();
    await analyzeFullSite();
  }, [a11yWizardAdapter, enableRTLDomOrder, editorHost, createDomOrderRuleData, biLogger, getResolvedError, t, analyzeFullSite, showOrganizeConfirmationModal]);
  return {
    handleAutoFix,
    handleManualFix
  };
};
//# sourceMappingURL=use-dom-order-fix.js.map